import {
  ExternalLink,
  Bot,
  History,
  LayoutDashboard,
  Menu,
  MoreHorizontal,
  PanelLeft,
  ScanSearch,
  Settings,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Spinner,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@opencloud/ui";
import { useEffect, useRef, useState } from "react";
import { personalAiAccountsAllowed, type AgentBootstrap } from "../bootstrap.js";
import { agentBrand } from "../brand.js";
import type {
  AgentAppView,
  AgentRelease,
  ConversationRun,
  ChatSnapshot as ConversationSnapshot,
  ConversationThread,
  IncidentView,
  PendingConversationMessage,
  SessionView,
} from "../types.js";
import {
  attachedTerminalOutcome,
  deploymentAwareSnapshot,
} from "../presentation.js";
import { AppIcon, ChatIcon } from "./AppIcon.js";
import type { ConversationRecord, ConversationSidebarUpdate } from "@opencloud/agent-protocol";
import { Composer } from "./Composer.js";
import type { ComposerRuntime } from "./ComposerRuntime.js";
import type { AutomaticFailureContext } from "./AutomaticFailureCard.js";
import { usesAppOwnerHarness } from "../harness-provider.js";
import { ContinuityStatus } from "./ContinuityStatus.js";
import type { ChatUploads } from "../types.js";
import { useAppConversation } from "../use-app-conversation.js";
import { ComposerTaskPlan } from "./ComposerTaskPlan.js";
import { latestConversationPlan } from "../task-presentation.js";
import { DeploymentStatus } from "./DeploymentStatus.js";
import { InlineRunStatus, RunActivity } from "./RunActivity.js";
import { Sidebar } from "./Sidebar.js";
import { TraceInspector } from "./TraceInspector.js";
import { Transcript, PendingMessage, WaitingIncidents } from "./Transcript.js";
import { SubAgentsPanel } from "./SubAgentsPanel.js";
import type { AgentConversationActivity } from "../use-agent-events.js";

export function Workspace({
  bootstrap,
  session,
  apps,
  workingAppIds,
  attentionAppIds,
  threadsByApp,
  app,
  thread,
  snapshot,
  incidents,
  releases,
  focusMessageId,
  pendingMessage,
  awaitingReply,
  liveText,
  draft,
  offline,
  stale,
  busy,
  stopping,
  onDraft,
  onHome,
  onNewApp,
  onSelectApp,
  onAgentSettings,
  onReleaseHistory,
  onLoadRelease,
  onSend,
  onStop,
  onAnswer,
  onRetry,
  onRetryMessage,
  onAllowProvider,
  onConnectProvider,
  onRetryAgentMessage = () => false,
  onReportProblem,
  onFixIncident,
  conversation: suppliedConversation,
  conversations,
  workingConversationIds,
  attentionConversationIds,
  conversationPreviews,
  conversationActivity,
  loadingConversation = false,
  onSelectConversation,
  onConversationChanged,
  contextControls,
  composerNote,
  canSend: canSendOverride,
  uploads,
  composerRuntime,
  automaticFailures,
}: {
  bootstrap: AgentBootstrap;
  session: SessionView;
  apps: readonly AgentAppView[];
  workingAppIds: ReadonlySet<string>;
  attentionAppIds?: ReadonlySet<string>;
  threadsByApp: ReadonlyMap<string, ConversationThread | null>;
  app: AgentAppView | null;
  thread: ConversationThread | null;
  snapshot: ConversationSnapshot | null;
  incidents: readonly IncidentView[];
  releases: readonly AgentRelease[];
  focusMessageId: string | null;
  pendingMessage: PendingConversationMessage | null;
  awaitingReply: boolean;
  liveText: string;
  draft: string;
  offline: boolean;
  stale: boolean;
  busy: boolean;
  stopping: boolean;
  onDraft: (draft: string) => void;
  onHome: () => void;
  onNewApp: () => void;
  onSelectApp: (
    app: AgentAppView,
    location?: { threadId: string; messageId?: string },
  ) => void;
  onAgentSettings: () => void;
  onReleaseHistory: () => void;
  onLoadRelease: (releaseId: string) => Promise<AgentRelease>;
  onSend: () => void;
  onStop: () => void;
  onAnswer: (answer: string) => void;
  onRetry: (runId: string) => void;
  onRetryMessage: () => void;
  onAllowProvider?: (() => void) | undefined;
  onConnectProvider?: (() => void) | undefined;
  onRetryAgentMessage?: (
    messageId: string,
  ) => boolean | Promise<boolean>;
  onReportProblem: (title: string, summary: string) => Promise<void>;
  /** Asks the app's Agent to fix an alert it didn't fix by itself. */
  onFixIncident?: ((incident: IncidentView) => Promise<void>) | undefined;
  conversation?: ConversationRecord | null;
  conversations?: readonly ConversationRecord[];
  workingConversationIds?: ReadonlySet<string>;
  attentionConversationIds?: ReadonlySet<string>;
  conversationPreviews?: ReadonlyMap<string, string>;
  conversationActivity?: ReadonlyMap<string, AgentConversationActivity>;
  loadingConversation?: boolean;
  onSelectConversation?: (conversation: ConversationRecord) => void;
  onConversationChanged?: (conversationId: string, conversation: ConversationRecord | null,
    change: ConversationSidebarUpdate) => void;
  contextControls?: import("react").ReactNode;
  /** One line under the composer, such as why no AI account can run. */
  composerNote?: import("react").ReactNode;
  canSend?: boolean;
  uploads?: ChatUploads | undefined;
  composerRuntime?: ComposerRuntime | undefined;
  /** App chats: the app's automatic choice and actions for automatic failure cards. */
  automaticFailures?: AutomaticFailureContext | undefined;
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const appConversation = useAppConversation(suppliedConversation === undefined ? app?.id ?? null : null);
  const conversation = suppliedConversation === undefined ? appConversation : suppliedConversation;
  const canSend = canSendOverride ?? conversation?.canSend === true;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const [subAgentsOpen, setSubAgentsOpen] = useState(false);
  const scrollFrameRef = useRef<HTMLDivElement>(null);
  const composerFooterRef = useRef<HTMLElement>(null);
  const presentedSnapshot = snapshot
    ? deploymentAwareSnapshot(snapshot)
    : null;
  const activeRun = latestActiveRoot(presentedSnapshot?.runs ?? []);
  const attachedOutcome = presentedSnapshot
    ? attachedTerminalOutcome(presentedSnapshot, releases)
    : null;
  const pendingConversation = !app || app.identityStatus === "pending";
  const candidatePlan = presentedSnapshot ? latestConversationPlan(presentedSnapshot) : null;
  const plan = candidatePlan?.run.task || candidatePlan?.run.trigger === "incident" ? null : candidatePlan;

  useEffect(() => {
    const frame = scrollFrameRef.current;
    const footer = composerFooterRef.current;
    if (!frame || !footer) return;
    let scrollFrame = 0;
    let firstMeasurement = true;
    const viewport = () =>
      frame.querySelector<HTMLElement>("[data-slot='scroll-area-viewport']") ??
      frame.querySelector<HTMLElement>(".a3-transcript");
    const updateFooterInset = () => {
      const scroller = viewport();
      const shouldFollow = firstMeasurement
        ? focusMessageId === null
        : scroller
          ? scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <=
            160
          : true;
      firstMeasurement = false;
      frame.style.setProperty(
        "--a3-floating-footer-height",
        `${Math.ceil(footer.getBoundingClientRect().height)}px`,
      );
      if (shouldFollow && scroller) {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = requestAnimationFrame(() => {
            scroller.scrollTop = scroller.scrollHeight;
          });
        });
      }
    };
    updateFooterInset();
    const observer = new ResizeObserver(updateFooterInset);
    observer.observe(footer);
    return () => {
      cancelAnimationFrame(scrollFrame);
      observer.disconnect();
      frame.style.removeProperty("--a3-floating-footer-height");
    };
  }, [focusMessageId]);
  const sidebar = (
    <Sidebar
      bootstrap={bootstrap}
      session={session}
      apps={apps}
      workingAppIds={workingAppIds}
      {...(attentionAppIds ? { attentionAppIds } : {})}
      threadsByApp={threadsByApp}
      currentApp={app}
      {...(conversations ? { conversations } : {})}
      {...(workingConversationIds ? { workingConversationIds } : {})}
      {...(attentionConversationIds ? { attentionConversationIds } : {})}
      {...(conversationPreviews ? { conversationPreviews } : {})}
      {...(conversationActivity ? { conversationActivity } : {})}
      {...(thread ? { currentConversationId: thread.id } : {})}
      {...(onSelectConversation ? { onSelectConversation: (conversation: ConversationRecord) => {
        setSheetOpen(false);
        onSelectConversation(conversation);
      } } : {})}
      {...(onConversationChanged ? { onConversationChanged } : {})}
      onHome={() => {
        setSheetOpen(false);
        onHome();
      }}
      onNewApp={() => {
        setSheetOpen(false);
        onNewApp();
      }}
      onSelectApp={(next, location) => {
        setSheetOpen(false);
        onSelectApp(next, location);
      }}
    />
  );

  return (
    <TooltipProvider>
    <div
      className="a3-theme a3-workspace-shell"
      data-oc-theme={agentBrand().themeId}
      data-design-system="opencloud-ui"
      data-sidebar-collapsed={sidebarCollapsed}
    >
      <aside className="a3-sidebar" aria-label="Chats and apps">
        {sidebar}
      </aside>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="left"
          className="a3-theme a3-sheet"
          data-oc-theme={agentBrand().themeId}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Chats and apps</SheetTitle>
            <SheetDescription>Open a recent chat or start a new chat.</SheetDescription>
          </SheetHeader>
          {sidebar}
        </SheetContent>
      </Sheet>

      <main className="a3-workspace" aria-label="Conversation">
        <header className="a3-header">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="a3-icon-button a3-mobile-menu"
            aria-label="Open chats and apps"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          >
            <Menu aria-hidden="true" />
          </Button>
          <Tooltip>
            <TooltipTrigger
              render={<Button type="button" size="icon" variant="ghost" className="a3-icon-button a3-desktop-sidebar-toggle" />}
              aria-label={sidebarCollapsed ? "Show chats" : "Hide chats"}
              onClick={() => setSidebarCollapsed((value) => !value)}
            >
              <PanelLeft aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>{sidebarCollapsed ? "Show chats" : "Hide chats"}</TooltipContent>
          </Tooltip>
          <div className="a3-thread-heading" data-scope={pendingConversation ? "pending" : "app"}>
            {app ? <AppIcon app={app} /> : <ChatIcon />}
            <div>
              <div className="a3-thread-title-row"><h1 className="a3-thread-title">
                {app?.name ?? thread?.title ?? "New chat"}
              </h1>{!pendingConversation && presentedSnapshot ? <DeploymentStatus snapshot={presentedSnapshot} stale={stale || offline} /> : null}</div>
              {app && !pendingConversation ? <span>{chatAudience(snapshot?.participation)}</span> : null}
            </div>
          </div>
          <div className="a3-header-actions">
            {/* An app without a release answers 503; offer it once it is live. */}
            {!pendingConversation && app?.appUrl &&
            (presentedSnapshot?.app?.activeDeploymentId ?? app.activeDeploymentId) ? (
              <Button
                render={<a href={app.appUrl} target="_blank" rel="noreferrer" />}
                className="a3-action a3-action-secondary"
                variant="outline"
                aria-label="Open app"
              >
                <ExternalLink aria-hidden="true" />
                <span className="a3-open-app-label">Open app</span>
              </Button>
            ) : null}
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger
                render={<Button type="button" size="icon" variant="ghost" className="a3-icon-button" />}
                aria-label={pendingConversation ? "Conversation options" : "App options"}
              >
                <MoreHorizontal aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="a3-conversation-menu">
                <ConversationMenu
                  ownerControls={snapshot?.participation?.role !== "builder"}
                  canManage={canSend}
                  appName={app?.name ?? thread?.title ?? "Chat"}
                  appAssigned={!pendingConversation}
                  dashboardUrl={app ? `${bootstrap.appsUrl}/${encodeURIComponent(app.id)}` : bootstrap.appsUrl}
                  onDismiss={() => undefined}
                  onAgentSettings={() => {
                    if (!canSend) return;
                    setMenuOpen(false);
                    onAgentSettings();
                  }}
                  onReleaseHistory={() => {
                    setMenuOpen(false);
                    onReleaseHistory();
                  }}
                  onRunEvidence={snapshot?.participation?.role === "builder" ? undefined : () => {
                    setMenuOpen(false);
                    setTraceOpen(true);
                  }}
                  onSubAgents={snapshot?.participation?.role === "builder" ? undefined : () => { setMenuOpen(false); setSubAgentsOpen(true); }}
                  onReportProblem={() => {
                    if (!canSend) return;
                    setMenuOpen(false);
                    setReportOpen(true);
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {stale ? (
          <div
            className="a3-reconnecting-pill"
            role="status"
            aria-live="polite"
          >
            <Spinner aria-label="Reconnecting" />
            <span>Reconnecting…</span>
          </div>
        ) : null}

        <div className="a3-scroll-frame" ref={scrollFrameRef}>
          {presentedSnapshot ? (
            <Transcript
              viewerUserId={session.userId}
              readOnly={!canSend}
              snapshot={presentedSnapshot}
              incidents={incidents}
              releases={releases}
              appUrl={app?.appUrl ?? null}
              attachedOutcomeRunId={attachedOutcome?.runId ?? null}
              onRetry={onRetry}
              onLoadRelease={onLoadRelease}
              focusMessageId={focusMessageId}
              pendingMessage={pendingMessage}
              liveText={liveText}
              onRetryMessage={onRetryMessage}
              onAllowProvider={onAllowProvider}
              onConnectProvider={onConnectProvider}
              personalAiAccounts={personalAiAccountsAllowed(bootstrap)}
              onRetryAgentMessage={onRetryAgentMessage}
              automaticFailures={automaticFailures}
              onFixIncident={onFixIncident}
              trailingContent={
                !activeRun?.task && activeRun?.trigger !== "incident" ? <InlineRunStatus
                  snapshot={presentedSnapshot}
                  liveText={liveText}
                  pending={awaitingReply}
                /> : null
              }
            />
          ) : loadingConversation ? (
            <div className="a3-transcript" role="status" aria-label="Loading conversation">
              <div className="a3-transcript-content a3-conversation-loading">
                <Skeleton className="h-16 w-3/5 rounded-2xl" />
                <Skeleton className="ml-auto h-12 w-2/5 rounded-2xl" />
                <Skeleton className="h-24 w-4/5 rounded-2xl" />
              </div>
            </div>
          ) : (
            <div className="a3-transcript">
              <div className="a3-transcript-content">
                {app && !pendingMessage ? <WaitingIncidents incidents={incidents} onFix={canSend ? onFixIncident : undefined} /> : null}
                {pendingMessage ? <PendingMessage message={pendingMessage} onRetry={onRetryMessage} onAllowProvider={onAllowProvider} onConnectProvider={onConnectProvider} personalAiAccounts={personalAiAccountsAllowed(bootstrap)} /> : <div className="a3-empty-transcript">
                  <h2>{app ? `Start a conversation about ${app.name ?? "this app"}` : "What would you like to explore?"}</h2>
                  <p>
                    Describe a change, ask how something works, or tell the agent
                    about a problem.
                  </p>
                </div>}
              </div>
            </div>
          )}

          <footer className="a3-composer-footer" ref={composerFooterRef}>
            {presentedSnapshot ? (
              <RunActivity
                readOnly={!canSend}
                snapshot={presentedSnapshot}
                liveText={liveText}
                attachedOutcome={attachedOutcome}
                onAnswer={onAnswer}
                onRetry={onRetry}
                personalAiAccounts={personalAiAccountsAllowed(bootstrap)}
              />
            ) : null}
            {plan ? <div className="a3-composer-details" role="group" aria-label="Conversation details">
              {plan ? <ComposerTaskPlan key={`${plan.run.id}:${plan.taskList.id}`} plan={plan} readOnly={!canSend}
                {...(attachedOutcome?.summary ? { failureSummary: attachedOutcome.summary } : {})}
                onRetry={onRetry} /> : null}
            </div> : null}
            {contextControls}
            <ContinuityStatus continuity={snapshot?.continuity} />
            <Composer
              uploads={!(snapshot?.thread ?? thread) || usesAppOwnerHarness((snapshot?.thread ?? thread)?.harnessVersion ?? "") ? uploads : undefined}
              canSend={canSend}
              canStop={conversation?.canCancel === true}
              draft={draft}
              setDraft={onDraft}
              runActive={Boolean(activeRun) || awaitingReply}
              offline={offline}
              busy={busy}
              stopping={stopping}
              onSend={onSend}
              onStop={onStop}
              runtime={composerRuntime}
            />
            {composerNote}
            {conversation && !conversation.canSend ? <p className="text-xs text-muted-foreground">This conversation is read-only. You don’t currently have access to send messages.</p> : null}
          </footer>
        </div>
      </main>

      {app && reportOpen && snapshot?.participation?.role !== "builder" ? (
        <ReportProblem
          app={app}
          onClose={() => setReportOpen(false)}
          onSubmit={async (title, summary) => {
            await onReportProblem(title, summary);
            setReportOpen(false);
          }}
        />
      ) : null}
      {app && snapshot?.participation?.role !== "builder" ? <SubAgentsPanel key={app.id} appId={app.id} appName={app.name ?? "this app"} open={subAgentsOpen} onOpenChange={setSubAgentsOpen} /> : null}
      {app && !pendingConversation && snapshot?.participation?.role !== "builder" ? (
        <TraceInspector
          appId={app.id}
          runs={snapshot?.runs ?? []}
          open={traceOpen}
          onOpenChange={setTraceOpen}
        />
      ) : null}
    </div>
    </TooltipProvider>
  );
}

export function ConversationMenu({
  ownerControls = true,
  canManage = true,
  appName = "App",
  appAssigned = true,
  dashboardUrl,
  onDismiss,
  onAgentSettings,
  onReleaseHistory,
  onRunEvidence,
  onSubAgents,
  onReportProblem,
}: {
  ownerControls?: boolean;
  canManage?: boolean;
  appName?: string;
  appAssigned?: boolean;
  dashboardUrl: string;
  onDismiss: () => void;
  onAgentSettings: () => void;
  onReleaseHistory: () => void;
  onRunEvidence?: (() => void) | undefined;
  onSubAgents?: (() => void) | undefined;
  onReportProblem: () => void;
}) {
  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel>{appName}</DropdownMenuLabel>
        {ownerControls && appAssigned && onSubAgents && <DropdownMenuItem onClick={onSubAgents}><Bot aria-hidden="true" /> Sub-Agents</DropdownMenuItem>}
        {appAssigned ? (
          <DropdownMenuItem onClick={onReleaseHistory}>
            <History aria-hidden="true" /> Release history
          </DropdownMenuItem>
        ) : null}
        {ownerControls && appAssigned && onRunEvidence ? (
          <DropdownMenuItem onClick={onRunEvidence}>
            <ScanSearch aria-hidden="true" /> Run evidence
          </DropdownMenuItem>
        ) : null}
        {/* App Admins change the app's AI here too (D1); the service enforces it. */}
        {canManage ? <DropdownMenuItem onClick={onAgentSettings}>
          <Settings aria-hidden="true" /> Agent settings
        </DropdownMenuItem> : null}
      </DropdownMenuGroup>
      {appAssigned ? <DropdownMenuSeparator /> : null}
      {appAssigned ? <DropdownMenuGroup>
        {ownerControls && canManage ? <DropdownMenuItem onClick={onReportProblem}>
          Report a problem
        </DropdownMenuItem> : null}
        {appAssigned ? (
          <DropdownMenuItem
            render={<a href={dashboardUrl} onClick={onDismiss} />}
          >
            <LayoutDashboard aria-hidden="true" /> Open app dashboard
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup> : null}
    </>
  );
}

/** Who else reads an app's chat: its admins, whom the owner adds from Sharing. */
function chatAudience(participation: { role: "owner" | "builder"; collaborationEnabled: boolean } | undefined): string {
  if (!participation?.collaborationEnabled) return "App conversation";
  return participation.role === "owner" ? "Admins you add can see this chat" : "Shared with the app's admins";
}

function ReportProblem({
  app,
  onClose,
  onSubmit,
}: {
  app: AgentAppView;
  onClose: () => void;
  onSubmit: (title: string, summary: string) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="a3-report-dialog">
        <DialogHeader>
          <p className="a3-eyebrow">{app.name ?? "New chat"}</p>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            Tell the agent what went wrong. It can investigate and make a
            policy-approved verified repair.
          </DialogDescription>
        </DialogHeader>
        <form
          className="a3-report-form"
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void onSubmit(title.trim(), summary.trim()).finally(() =>
              setBusy(false),
            );
          }}
        >
          <Field>
            <FieldLabel htmlFor="report-problem-title">What went wrong?</FieldLabel>
            <Input
              id="report-problem-title"
              required
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="report-problem-summary">What did you see?</FieldLabel>
            <Textarea
              id="report-problem-summary"
              required
              maxLength={2_000}
              rows={4}
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
            >
              {busy ? "Sending…" : "Send report"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function latestActiveRoot(
  runs: readonly ConversationRun[],
): ConversationRun | null {
  return (
    [...runs]
      .reverse()
      .find(
        (run) =>
          run.parentRunId === null &&
          run.phase !== "finished" &&
          !["idle", "completed", "failed", "cancelled"].includes(run.status),
      ) ?? null
  );
}
